import { useEffect, useState } from "react";
import { Button, Drawer, Hairline, Input } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { SupplierDTO, SupplierUserDTO } from "@wmart/shared-types";

/**
 * Manage portal logins for a supplier. Loads users on open, lets the admin
 * add a new login (POST) or remove an existing one (DELETE). Account-ready
 * email is sent automatically by the API — admin shares the password OOB.
 */
export function SupplierUsersDrawer({
  supplier,
  onClose,
}: {
  supplier: SupplierDTO;
  onClose: () => void;
}) {
  const { notify, notifyProblem } = useToast();

  // ---- users list ----
  const [users, setUsers] = useState<SupplierUserDTO[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // ---- add-user form ----
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formName, setFormName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const res = await api.get<{ users: SupplierUserDTO[] }>(
        `/v1/admin/suppliers/${supplier.id}/users`,
      );
      setUsers(res.users);
    } catch {
      notify("error", "Failed to load portal users");
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.id]);

  async function removeUser(u: SupplierUserDTO) {
    if (!window.confirm(`Remove portal access for "${u.email}"?`)) return;
    setRemovingId(u.id);
    try {
      await api.delete(`/v1/admin/suppliers/${supplier.id}/users/${u.id}`);
      notify("success", "Portal access removed");
      void loadUsers();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Remove failed");
    } finally {
      setRemovingId(null);
    }
  }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/v1/admin/suppliers/${supplier.id}/users`, {
        email: formEmail.trim(),
        password: formPassword,
        name: formName.trim() || undefined,
      });
      notify("success", "Account created — sign-in details emailed");
      setFormEmail("");
      setFormPassword("");
      setFormName("");
      void loadUsers();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Create failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Portal users — ${supplier.name}`}
      width={480}
    >
      <div className="flex flex-col gap-6">
        {/* Existing users */}
        <section className="flex flex-col gap-3">
          <h3 className="text-body-sm font-medium text-ink-700">
            Current logins
          </h3>
          {loadingUsers ? (
            <p className="text-body-sm text-ink-500">Loading…</p>
          ) : users.length === 0 ? (
            <p className="text-body-sm text-ink-500">No portal logins yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-ink-100 rounded-sm border border-ink-200">
              {users.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-body-sm text-ink-900 truncate">
                      {u.email}
                    </p>
                    {u.name ? (
                      <p className="text-caption text-ink-500">{u.name}</p>
                    ) : null}
                    <p className="text-caption text-ink-400">
                      Added{" "}
                      {new Date(u.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Remove ${u.email}`}
                    disabled={removingId === u.id}
                    loading={removingId === u.id}
                    onClick={() => void removeUser(u)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <Hairline />

        {/* Add user form */}
        <section className="flex flex-col gap-3">
          <h3 className="text-body-sm font-medium text-ink-700">Add user</h3>
          <form onSubmit={(e) => void addUser(e)} className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              required
              autoComplete="off"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
            />
            <Input
              label="Initial password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              helper="Share this password with the supplier directly — the email tells them to expect it."
            />
            <Input
              label={
                <>
                  Name{" "}
                  <span className="font-normal text-ink-400">(optional)</span>
                </>
              }
              type="text"
              autoComplete="off"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button type="submit" disabled={submitting} loading={submitting}>
                Add user
              </Button>
            </div>
          </form>
        </section>
      </div>
    </Drawer>
  );
}
